import { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { seedIfEmpty } from "./seed";
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, where
} from "firebase/firestore";
import {
  signInWithEmailAndPassword, signOut, onAuthStateChanged
} from "firebase/auth";

// ─── MONSTER STAGES ───────────────────────────────────────────
const HOME_MONSTERS = [
  { label:"LATENT",       emoji:"💤", color:"#555",    bg:"rgba(255,255,255,0.02)", border:"#2a2a2a"   },
  { label:"GETTING UGLY", emoji:"👁️",  color:"#A8FF78", bg:"rgba(168,255,120,0.05)", border:"#A8FF7844" },
  { label:"FURIOUS",      emoji:"🔥", color:"#FFD93D", bg:"rgba(255,217,61,0.06)",  border:"#FFD93D44" },
  { label:"LURKING",      emoji:"🫦", color:"#FF8C42", bg:"rgba(255,140,66,0.07)",  border:"#FF8C4255" },
  { label:"LEGENDARY",    emoji:"☠️",  color:"#FF3333", bg:"rgba(255,51,51,0.09)",   border:"#FF333366" },
];
const AKZ_MONSTERS = [
  { label:"LATENT",    emoji:"💤", color:"#555",    bg:"rgba(255,255,255,0.02)", border:"#2a2a2a"   },
  { label:"OVERDUE",   emoji:"👁️",  color:"#A8FF78", bg:"rgba(168,255,120,0.05)", border:"#A8FF7844" },
  { label:"BURNING",   emoji:"🔥", color:"#FFD93D", bg:"rgba(255,217,61,0.06)",  border:"#FFD93D44" },
  { label:"CRITICAL",  emoji:"🫦", color:"#FF8C42", bg:"rgba(255,140,66,0.07)",  border:"#FF8C4255" },
  { label:"LEGENDARY", emoji:"☠️",  color:"#FF3333", bg:"rgba(255,51,51,0.09)",   border:"#FF333366" },
];
function getMonsterIdx(days) {
  if (days < 7)   return 0;
  if (days < 30)  return 1;
  if (days < 90)  return 2;
  if (days < 180) return 3;
  return 4;
}
function getAKZMonsterIdx(task) {
  if (!task.dueDate) return getMonsterIdx(getDays(task.date));
  const daysLeft = Math.ceil((new Date(task.dueDate) - new Date()) / 86400000);
  if (daysLeft > 7)  return 0;
  if (daysLeft > 3)  return 1;
  if (daysLeft > 1)  return 2;
  if (daysLeft >= 0) return 3;
  return 4;
}
function getDueDateLabel(dueDate) {
  if (!dueDate) return null;
  const daysLeft = Math.ceil((new Date(dueDate) - new Date()) / 86400000);
  if (daysLeft < 0)  return { text: `${Math.abs(daysLeft)}D OVERDUE`, color:"#FF3333" };
  if (daysLeft === 0) return { text: "DUE TODAY", color:"#FF3333" };
  if (daysLeft === 1) return { text: "DUE TOMORROW", color:"#FF8C42" };
  if (daysLeft <= 7)  return { text: `DUE IN ${daysLeft}D`, color:"#FFD93D" };
  return { text: `DUE ${new Date(dueDate).toLocaleDateString("es-MX",{month:"short",day:"numeric"})}`, color:"#555" };
}

// ─── CONSTANTS ────────────────────────────────────────────────
const ANA_COLOR = "#7EB8C9";
const PEOPLE = {
  Keops: { color:"#FFD93D", label:"Keops" },
  Ana:   { color:ANA_COLOR, label:"Ana"   },
  TODOS: { color:"#C4C4C4", label:"Juntos" },
};
const HOME_CATEGORIES = {
  casa:        { label:"Casa & Limpieza",      emoji:"🧹", color:ANA_COLOR  },
  instalacion: { label:"Instalaciones",        emoji:"🔧", color:"#FFD93D" },
  personal:    { label:"Personal",             emoji:"📋", color:"#A8FF78" },
  compras:     { label:"Compras",              emoji:"🛒", color:"#6BCEFF" },
  acciones:    { label:"Acciones & Gestiones", emoji:"⚡", color:"#C4A4FF" },
};
const CLIENT_DEFAULT_COLOR = "#6BCEFF";
const AKZ_ACCENT = "#A8FF78";

// ─── OWNERS ───────────────────────────────────────────────────
const OWNER_UIDS = [
  "6jXhRhpYfGNUIw9NvHcDGoUfMbZ2", // Keops
  "HWgbuZVDIvVl9tTlBltJNy2rArQ2", // Ana
];
function isOwner(uid) { return OWNER_UIDS.includes(uid); }

// Collab colors — palette separate from monster system, no semantic collision
const COLLAB_COLORS = ["#B8A9FF","#FFB347","#A0D8EF","#F4A7B9","#FFCCBC","#E1BEE7","#B2EBF2","#C8E6C9"];
function collabColor(uid) {
  if (!uid || typeof uid !== "string") return COLLAB_COLORS[0];
  let hash = 0;
  for (let i = 0; i < uid.length; i++) hash = uid.charCodeAt(i) + ((hash << 5) - hash);
  return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length];
}
function initials(name="?") {
  return name.trim().split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
}

function getDays(dateStr) { return Math.floor((new Date() - new Date(dateStr)) / 86400000); }
function today() { return new Date().toISOString().split("T")[0]; }
// Normalize who — always returns array regardless of legacy string format
function whoArr(who) { if (!who) return []; return Array.isArray(who) ? who : [who]; }
function minutesDone(task) {
  if (!task.done || !task.doneAt) return null;
  return Math.floor((Date.now() - task.doneAt) / 60000);
}
// Grace period: 4 horas = 240 minutos
function isPendingArchive(task) {
  if (!task.done) return false;
  const mins = minutesDone(task);
  if (mins === null) return false;
  return mins < 240;
}
function hexValid(h) { return /^#[0-9A-Fa-f]{6}$/.test(h); }

// ─── LOGIN SCREEN ─────────────────────────────────────────────
function LoginScreen({ onAuth }) {
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake]     = useState(false);

  const attempt = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onAuth();
    } catch (e) {
      setError("WRONG EMAIL OR PASSWORD");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&subset=latin,latin-ext&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:#0A0908; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
        .shake { animation:shake 0.4s ease; }
        @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }
      `}</style>
      <div style={{ minHeight:"100vh", background:"#0A0908", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 24px", fontFamily:"'Inter', sans-serif" }}>
        <div style={{ marginBottom:48, textAlign:"center" }}>
          <div style={{ display:"flex", alignItems:"baseline", justifyContent:"center", gap:4 }}>
            <span style={{ fontFamily:"'Archivo Black', sans-serif", fontSize:48, color:"#F0EDE8", letterSpacing:-2, lineHeight:1 }}>ADULTING</span>
            <span style={{ fontFamily:"'DM Mono', monospace", fontSize:16, color:ANA_COLOR, paddingBottom:8, alignSelf:"flex-end" }}>.monster</span>
          </div>
          <div style={{ marginTop:12, fontSize:11, color:"#333", letterSpacing:3, textTransform:"uppercase", fontFamily:"'Archivo Black', sans-serif" }}>Ana × Keops</div>
        </div>
        <div style={{ fontSize:48, marginBottom:32, animation:"pulse 2s ease infinite" }}>👹</div>
        <div className={shake ? "shake" : ""} style={{ width:"100%", maxWidth:320, display:"flex", flexDirection:"column", gap:10 }}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && attempt()}
            placeholder="email"
            autoFocus
            style={{ width:"100%", background:"#111", border:`2px solid ${error?"#FF3333":"#2a2a2a"}`, color:"#F0EDE8", padding:"14px 16px", fontSize:15, fontFamily:"'DM Mono', monospace", outline:"none", letterSpacing:1, transition:"border-color 0.2s" }}
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && attempt()}
            placeholder="password"
            style={{ width:"100%", background:"#111", border:`2px solid ${error?"#FF3333":"#2a2a2a"}`, color:"#F0EDE8", padding:"14px 16px", fontSize:15, fontFamily:"'DM Mono', monospace", outline:"none", letterSpacing:1, transition:"border-color 0.2s" }}
          />
          {error && <div style={{ textAlign:"center", fontSize:11, color:"#FF3333", fontFamily:"'Archivo Black', sans-serif", letterSpacing:1 }}>{error}</div>}
        </div>
        <button
          onClick={attempt}
          disabled={loading}
          style={{ marginTop:16, width:"100%", maxWidth:320, background:loading?"#1a1a1a":ANA_COLOR, border:"none", cursor:loading?"default":"pointer", padding:"14px", color:loading?"#333":"#000", fontFamily:"'Archivo Black', sans-serif", fontSize:14, letterSpacing:2, textTransform:"uppercase", transition:"background 0.2s" }}
        >
          {loading ? "..." : "ENTER THE MONSTER"}
        </button>
        <div style={{ marginTop:32, fontSize:10, color:"#2a2a2a", fontFamily:"'DM Mono', monospace" }}>adulting.monster — AKZ studio</div>
      </div>
    </>
  );
}

// ─── AVATARS ──────────────────────────────────────────────────
function AvatarKeops({ size=28 }) {
  const c="#FFD93D";
  return <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{flexShrink:0}}>
    <circle cx="20" cy="20" r="18" stroke={c} strokeWidth="1" strokeDasharray="90 24" strokeLinecap="round" opacity="0.5"/>
    <line x1="13" y1="10" x2="13" y2="30" stroke={c} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="13" y1="20" x2="26" y2="11" stroke={c} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="13" y1="20" x2="27" y2="30" stroke={c} strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="28" cy="20" r="2" fill={c}/>
  </svg>;
}
function AvatarAna({ size=28 }) {
  const c=ANA_COLOR;
  return <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{flexShrink:0}}>
    <rect x="3" y="3" width="34" height="34" rx="10" stroke={c} strokeWidth="1.5" opacity="0.5"/>
    <line x1="20" y1="9" x2="10" y2="31" stroke={c} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="20" y1="9" x2="30" y2="31" stroke={c} strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="14" y1="23" x2="26" y2="23" stroke={c} strokeWidth="2" strokeLinecap="round"/>
    <circle cx="20" cy="9" r="2.5" fill={c} opacity="0.7"/>
  </svg>;
}
function Avatar({ who, size=28, collabs=[] }) {
  const w = Array.isArray(who) ? who[0] : who;
  if (w==="Keops") return <AvatarKeops size={size}/>;
  if (w==="Ana")   return <AvatarAna size={size}/>;
  if (w==="TODOS") return <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{flexShrink:0}}>
    <circle cx="20" cy="20" r="17" stroke="#C4C4C4" strokeWidth="1.5" opacity="0.5"/>
    <circle cx="20" cy="20" r="4" stroke="#C4C4C4" strokeWidth="1.8"/>
    <line x1="20" y1="10" x2="20" y2="14" stroke="#C4C4C4" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20" y1="26" x2="20" y2="30" stroke="#C4C4C4" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="10" y1="20" x2="14" y2="20" stroke="#C4C4C4" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="26" y1="20" x2="30" y2="20" stroke="#C4C4C4" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>;
  const collab = collabs.find(c => c.uid === w);
  if (collab) return <AvatarCollab name={collab.name} uid={collab.uid} size={size}/>;
  return <AvatarCollab name={(w||"?").slice(0,2)} uid={w||"x"} size={size}/>;
}

// ─── COLLAB AVATAR ────────────────────────────────────────────
function AvatarCollab({ name, uid, size=28 }) {
  const color = collabColor(uid||"x");
  const ini = initials(name);
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" style={{flexShrink:0}}>
      <rect x="2" y="2" width="36" height="36" fill={color+"22"} stroke={color} strokeWidth="1.5"/>
      <text x="20" y="26" textAnchor="middle" fontFamily="'Archivo Black', sans-serif" fontSize="14" fill={color} fontWeight="900">{ini}</text>
    </svg>
  );
}

// ─── COLLAB WELCOME SCREEN ────────────────────────────────────
function CollabWelcome({ collab, onContinue }) {
  const color = collabColor(collab.uid||"x");
  const ini = initials(collab.name);
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:#0A0908; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        .fade { animation: fadeUp 0.6s ease forwards; }
      `}</style>
      <div style={{minHeight:"100vh",background:"#0A0908",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px",fontFamily:"'Inter', sans-serif"}}>
        {/* Wordmark */}
        <div className="fade" style={{marginBottom:48,textAlign:"center",animationDelay:"0s"}}>
          <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",gap:4}}>
            <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:36,color:"#F0EDE8",letterSpacing:-1,lineHeight:1}}>ADULTING</span>
            <span style={{fontFamily:"'DM Mono', monospace",fontSize:14,color:"#7EB8C9",paddingBottom:6,alignSelf:"flex-end"}}>.monster</span>
          </div>
        </div>

        {/* Avatar grande */}
        <div className="fade" style={{animationDelay:"0.1s",marginBottom:24}}>
          <svg width={96} height={96} viewBox="0 0 40 40" fill="none">
            <rect x="1" y="1" width="38" height="38" fill={color+"22"} stroke={color} strokeWidth="1.5"/>
            <text x="20" y="27" textAnchor="middle" fontFamily="'Archivo Black', sans-serif" fontSize="16" fill={color} fontWeight="900">{ini}</text>
          </svg>
        </div>

        {/* Nombre */}
        <div className="fade" style={{animationDelay:"0.2s",textAlign:"center",marginBottom:12}}>
          <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:28,color:"#F0EDE8",letterSpacing:-0.5,lineHeight:1}}>{collab.name.toUpperCase()}</div>
        </div>

        {/* Mensaje */}
        <div className="fade" style={{animationDelay:"0.3s",textAlign:"center",marginBottom:48,maxWidth:320}}>
          <div style={{fontSize:13,color:"#555",fontFamily:"'DM Mono', monospace",lineHeight:1.6}}>
            tienes {collab.taskCount||0} task{(collab.taskCount||0)!==1?"s":""} esperando.
          </div>
        </div>

        {/* CTA */}
        <div className="fade" style={{animationDelay:"0.4s",width:"100%",maxWidth:320}}>
          <button onClick={onContinue} style={{width:"100%",background:color,border:"none",cursor:"pointer",padding:"14px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>
            VER MIS TASKS
          </button>
        </div>

        <div className="fade" style={{animationDelay:"0.5s",marginTop:32,fontSize:10,color:"#2a2a2a",fontFamily:"'DM Mono', monospace"}}>
          adulting.monster — AKZ studio
        </div>
      </div>
    </>
  );
}

// ─── RADIAL PROGRESS ──────────────────────────────────────────
function RadialProgress({ tasks }) {
  const size=110, cx=55, cy=55, R=42, R2=28;
  const circ=2*Math.PI*R, circ2=2*Math.PI*R2;
  const total=tasks.length||1, done=tasks.filter(t=>t.done).length, pct=done/total;
  const anaDone=tasks.filter(t=>t.who==="Ana"&&t.done).length;
  const keoDone=tasks.filter(t=>t.who==="Keops"&&t.done).length;
  const todDone=tasks.filter(t=>t.who==="TODOS"&&t.done).length;
  const anaT=tasks.filter(t=>t.who==="Ana").length;
  const keoT=tasks.filter(t=>t.who==="Keops").length;
  const todT=tasks.filter(t=>t.who==="TODOS").length;
  function seg(sf,lf,df,color){ const u=lf-0.06; if(u<=0)return null; const f=circ2*u*df; return{da:`${f} ${circ2-f}`,do:-(sf*circ2),color,op:df>0?1:0.12}; }
  const anaF=anaT/total,keoF=keoT/total,todF=todT/total;
  const s1=seg(0,anaF,anaDone/Math.max(anaT,1),ANA_COLOR);
  const s2=seg(anaF,keoF,keoDone/Math.max(keoT,1),"#FFD93D");
  const s3=seg(anaF+keoF,todF,todDone/Math.max(todT,1),"#C4C4C4");
  return (
    <div style={{position:"relative",width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
        <circle cx={cx} cy={cy} r={R} stroke="#222" strokeWidth="7" fill="none"/>
        <circle cx={cx} cy={cy} r={R} stroke="url(#pg)" strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ*(1-pct)} style={{transition:"stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)"}}/>
        <circle cx={cx} cy={cy} r={R2} stroke="#1a1a1a" strokeWidth="5" fill="none"/>
        {[s1,s2,s3].map((s,i)=>s&&<circle key={i} cx={cx} cy={cy} r={R2} stroke={s.color} strokeWidth="5" fill="none" strokeDasharray={s.da} strokeDashoffset={s.do} opacity={s.op}/>)}
        <defs><linearGradient id="pg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={ANA_COLOR}/><stop offset="100%" stopColor="#FFD93D"/></linearGradient></defs>
      </svg>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
        <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:30,color:"#fff",lineHeight:1}}>{Math.round(pct*100)}</span>
        <span style={{fontSize:9,color:"#555",letterSpacing:1}}>%</span>
      </div>
    </div>
  );
}

// ─── MONSTER BADGE ────────────────────────────────────────────
function MonsterBadge({ days, mode="home" }) {
  const stages = mode==="akz" ? AKZ_MONSTERS : HOME_MONSTERS;
  const idx = getMonsterIdx(days);
  if (idx===0) return null;
  const m = stages[idx];
  return (
    <span style={{ fontSize:10, fontWeight:800, letterSpacing:1, color:m.color, background:m.bg, border:`1px solid ${m.border}`, padding:"2px 7px 2px 5px", textTransform:"uppercase", flexShrink:0, fontFamily:"'Archivo Black', sans-serif", display:"flex", alignItems:"center", gap:4 }}>
      <span style={{fontSize:12}}>{m.emoji}</span>{m.label}
    </span>
  );
}

// ─── SHARED INPUT STYLES ──────────────────────────────────────
const inputStyle = { width:"100%", background:"#111", border:"2px solid #2a2a2a", color:"#F0EDE8", padding:"10px 12px", fontSize:15, fontFamily:"'Inter', sans-serif", outline:"none", borderRadius:0 };
const labelStyle = { fontSize:10, fontWeight:700, letterSpacing:2, color:"#555", textTransform:"uppercase", display:"block", marginBottom:4, fontFamily:"'Archivo Black', sans-serif" };

// ─── HOME TASK MODAL ─────────────────────────────────────────
function HomeTaskModal({ onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || { task:"", detail:"", who:"Keops", priority:"MID", cat:"casa", date:today() });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const valid = form.task.trim().length > 0;
  const SelectBtn = ({field,value,label,color}) => (
    <button onClick={()=>set(field,value)} style={{ padding:"5px 10px", border:`2px solid ${form[field]===value?(color||"#F0EDE8"):"#2a2a2a"}`, background:form[field]===value?(color||"#F0EDE8")+"22":"transparent", color:form[field]===value?(color||"#F0EDE8"):"#555", fontSize:10, fontWeight:700, letterSpacing:1, cursor:"pointer", fontFamily:"'Archivo Black', sans-serif", textTransform:"uppercase" }}>{label}</button>
  );
  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${isEdit?"#FFD93D":ANA_COLOR}`,padding:"24px 20px 40px",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>{isEdit?"✏️ EDIT TASK":"+ NEW TASK"}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Task *</label><input value={form.task} onChange={e=>set("task",e.target.value)} placeholder="¿Qué hay que hacer?" style={inputStyle} autoFocus/></div>
          <div><label style={labelStyle}>Notes</label><input value={form.detail} onChange={e=>set("detail",e.target.value)} placeholder="Detalles, horarios, links..." style={inputStyle}/></div>
          <div><label style={labelStyle}>Date</label><input type="date" value={form.date} onChange={e=>set("date",e.target.value)} style={{...inputStyle,colorScheme:"dark"}}/></div>
          <div><label style={labelStyle}>Who?</label><div style={{display:"flex",gap:6}}><SelectBtn field="who" value="Ana" label="Ana" color={ANA_COLOR}/><SelectBtn field="who" value="Keops" label="Keops" color="#FFD93D"/><SelectBtn field="who" value="TODOS" label="Juntos" color="#C4C4C4"/></div></div>
          <div><label style={labelStyle}>Category</label><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{Object.entries(HOME_CATEGORIES).map(([k,v])=><SelectBtn key={k} field="cat" value={k} label={`${v.emoji} ${v.label}`} color={v.color}/>)}</div></div>
          <div><label style={labelStyle}>Priority</label><div style={{display:"flex",gap:6}}><SelectBtn field="priority" value="LOW" label="Low" color="#A8FF78"/><SelectBtn field="priority" value="MID" label="Mid" color="#FFD93D"/><SelectBtn field="priority" value="HIGH" label="Urgent" color="#FF3333"/></div></div>
          <button onClick={()=>valid&&onSave(form)} style={{width:"100%",padding:"13px",background:valid?(isEdit?"#FFD93D":ANA_COLOR):"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>{isEdit?"SAVE CHANGES":"ADD TASK"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── AKZ TASK MODAL ──────────────────────────────────────────
function AKZTaskModal({ onClose, onSave, initial, clients, projects, collabs=[], defaultType, isEdit=false }) {
  const initWho = initial ? whoArr(initial.who) : ["Keops"];
  const [form, setForm] = useState(initial ? {...initial, who: initWho} : { task:"", detail:"", who:["Keops"], priority:"MID", date:today(), dueDate:"", type: defaultType||"action", clientId:"", projectId:"" });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleWho = (id) => {
    setForm(f => {
      const cur = whoArr(f.who);
      return {...f, who: cur.includes(id) ? (cur.length>1 ? cur.filter(x=>x!==id) : cur) : [...cur, id]};
    });
  };
  const isWhoSelected = (id) => whoArr(form.who).includes(id);
  const valid = form.task.trim().length > 0;
  const SelectBtn = ({field,value,label,color}) => (
    <button onClick={()=>set(field,value)} style={{padding:"5px 10px",border:`2px solid ${form[field]===value?(color||"#F0EDE8"):"#2a2a2a"}`,background:form[field]===value?(color||"#F0EDE8")+"22":"transparent",color:form[field]===value?(color||"#F0EDE8"):"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>{label}</button>
  );
  const WhoBtn = ({id, label, color}) => {
    const selected = isWhoSelected(id);
    return (
      <button onClick={()=>toggleWho(id)} style={{padding:"5px 10px",border:`2px solid ${selected?color:"#2a2a2a"}`,background:selected?color+"22":"transparent",color:selected?color:"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase",display:"flex",alignItems:"center",gap:4}}>
        {selected && <span style={{width:6,height:6,background:color,display:"inline-block",borderRadius:"50%",flexShrink:0}}/>}
        {label}
      </button>
    );
  };
  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${isEdit?"#FFD93D":AKZ_ACCENT}`,padding:"24px 20px 40px",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>{isEdit?"✏️ EDIT":"+ NEW ACTION"}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Task *</label><input value={form.task} onChange={e=>set("task",e.target.value)} placeholder="What needs to be done?" style={inputStyle} autoFocus/></div>
          <div><label style={labelStyle}>Notes</label><input value={form.detail} onChange={e=>set("detail",e.target.value)} placeholder="Context, links, deadlines..." style={inputStyle}/></div>
          <div><label style={labelStyle}>Date added</label><input type="date" value={form.date} onChange={e=>set("date",e.target.value)} style={{...inputStyle,colorScheme:"dark"}}/></div>
          <div>
            <label style={labelStyle}>Due date <span style={{color:"#333",fontWeight:400,letterSpacing:0,textTransform:"none",fontFamily:"'Inter', sans-serif"}}>— optional</span></label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input type="date" value={form.dueDate||""} onChange={e=>set("dueDate",e.target.value)} style={{...inputStyle,colorScheme:"dark",flex:1}}/>
              {form.dueDate&&<button onClick={()=>set("dueDate","")} style={{background:"none",border:"1px solid #2a2a2a",color:"#555",padding:"8px 10px",cursor:"pointer",fontSize:11,fontFamily:"'Archivo Black', sans-serif",flexShrink:0}}>✕</button>}
            </div>
          </div>
          <div><label style={labelStyle}>Type</label><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <SelectBtn field="type" value="action"   label="⚡ Action"           color="#FFD93D"/>
            <SelectBtn field="type" value="project"  label="🔧 Internal Project" color={AKZ_ACCENT}/>
            <SelectBtn field="type" value="client"   label="👤 Client"           color={CLIENT_DEFAULT_COLOR}/>
          </div></div>
          {form.type==="client" && clients.length>0 && (
            <div><label style={labelStyle}>Client</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {clients.map(c=><button key={c.id} onClick={()=>set("clientId",c.id)} style={{padding:"5px 10px",border:`2px solid ${form.clientId===c.id?(c.color||CLIENT_DEFAULT_COLOR):"#2a2a2a"}`,background:form.clientId===c.id?(c.color||CLIENT_DEFAULT_COLOR)+"22":"transparent",color:form.clientId===c.id?(c.color||CLIENT_DEFAULT_COLOR):"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>{c.name}</button>)}
              </div>
            </div>
          )}
          {form.type==="project" && projects.length>0 && (
            <div><label style={labelStyle}>Project</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {projects.map(p=><button key={p.id} onClick={()=>set("projectId",p.id)} style={{padding:"5px 10px",border:`2px solid ${form.projectId===p.id?AKZ_ACCENT:"#2a2a2a"}`,background:form.projectId===p.id?AKZ_ACCENT+"22":"transparent",color:form.projectId===p.id?AKZ_ACCENT:"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>{p.name}</button>)}
              </div>
            </div>
          )}
          <div>
            <label style={labelStyle}>Who? <span style={{color:"#333",fontWeight:400,letterSpacing:0,textTransform:"none",fontFamily:"'Inter', sans-serif"}}>— select one or more</span></label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <WhoBtn id="Ana"   label="Ana"    color={ANA_COLOR}/>
              <WhoBtn id="Keops" label="Keops"  color="#FFD93D"/>
              {collabs.map(c => <WhoBtn key={c.uid} id={c.uid} label={c.name.split(" ")[0]} color={collabColor(c.uid)}/>)}
            </div>
          </div>
          <div><label style={labelStyle}>Priority</label><div style={{display:"flex",gap:6}}><SelectBtn field="priority" value="LOW" label="Low" color="#A8FF78"/><SelectBtn field="priority" value="MID" label="Mid" color="#FFD93D"/><SelectBtn field="priority" value="HIGH" label="Urgent" color="#FF3333"/></div></div>
          <button onClick={()=>valid&&onSave(form)} style={{width:"100%",padding:"13px",background:valid?(isEdit?"#FFD93D":AKZ_ACCENT):"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>{isEdit?"SAVE CHANGES":"ADD"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── NEW CLIENT MODAL ─────────────────────────────────────────
function NewClientModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("");
  const valid = name.trim().length > 0;
  const resolvedColor = hexValid(color) ? color : CLIENT_DEFAULT_COLOR;
  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${CLIENT_DEFAULT_COLOR}`,padding:"24px 20px 40px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>👤 NEW CLIENT</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Client name *</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="Nombre del cliente" style={inputStyle} autoFocus/></div>
          <div>
            <label style={labelStyle}>Brand color (optional)</label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input value={color} onChange={e=>setColor(e.target.value)} placeholder="#6BCEFF" style={{...inputStyle,width:"auto",flex:1}}/>
              <div style={{width:36,height:36,background:resolvedColor,flexShrink:0,border:"2px solid #2a2a2a"}}/>
            </div>
            <div style={{fontSize:10,color:"#444",marginTop:4,fontFamily:"'DM Mono', monospace"}}>Leave empty for default color</div>
          </div>
          <button onClick={()=>valid&&onSave({name:name.trim(),color:resolvedColor})} style={{width:"100%",padding:"13px",background:valid?CLIENT_DEFAULT_COLOR:"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>ADD CLIENT</button>
        </div>
      </div>
    </div>
  );
}

// ─── NEW PROJECT MODAL ────────────────────────────────────────
function NewProjectModal({ onClose, onSave }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const valid = name.trim().length > 0;
  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${AKZ_ACCENT}`,padding:"24px 20px 40px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>🔧 NEW INTERNAL PROJECT</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Project name *</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. adulting.monster, AKZ branding..." style={inputStyle} autoFocus/></div>
          <div><label style={labelStyle}>Description</label><input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="What's this project about?" style={inputStyle}/></div>
          <button onClick={()=>valid&&onSave({name:name.trim(),desc:desc.trim()})} style={{width:"100%",padding:"13px",background:valid?AKZ_ACCENT:"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>CREATE PROJECT</button>
        </div>
      </div>
    </div>
  );
}

// ─── TASK CARD ────────────────────────────────────────────────
function TaskCard({ task, onToggle, onDelete, onEdit, mode="home", isArchive=false, collabs=[] }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [, forceUpdate] = useState(0);
  const days = getDays(task.date);
  const mIdx = task.done ? 0 : (mode==="akz" ? getAKZMonsterIdx(task) : getMonsterIdx(days));
  const stages = mode==="akz" ? AKZ_MONSTERS : HOME_MONSTERS;
  const dueBadge = mode==="akz" && !task.done && task.dueDate ? getDueDateLabel(task.dueDate) : null;
  const monster = stages[mIdx];
  const pending = isPendingArchive(task);
  const minsLeft = pending && task.doneAt ? Math.max(0, 240 - Math.floor((Date.now() - task.doneAt) / 60000)) : 0;

  // Multi-who support
  const whoList = whoArr(task.who);
  const firstWho = whoList[0] || "Keops";
  const firstCollab = collabs.find(c => c.uid === firstWho);
  const firstPerson = PEOPLE[firstWho] || { color: firstCollab ? collabColor(firstWho) : "#C4C4C4", label: firstCollab ? firstCollab.name.split(" ")[0] : firstWho.slice(0,8) };
  const borderColor = task.done ? "#3a3a3a" : firstPerson.color;

  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => forceUpdate(n => n+1), 30000);
    return () => clearInterval(t);
  }, [pending]);

  return (
    <div style={{ position:"relative", overflow:"hidden", border:`2px solid ${task.done?"#2e2e2e":monster.border}`, borderLeft:`4px solid ${borderColor}`, background:task.done?"transparent":monster.bg, padding:"12px 12px 10px 14px", opacity:task.done?0.45:1, transition:"all 0.12s ease" }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:6}}>
        <span onClick={()=>!isArchive&&onToggle(task.id)} style={{fontFamily:"'Archivo Black', sans-serif",fontSize:15,letterSpacing:0.2,cursor:isArchive?"default":"pointer",color:task.done?"#666":"#F0EDE8",textDecoration:task.done?"line-through #444":"none",lineHeight:1.3,flex:1}}>{task.task}</span>
        <div style={{display:"flex",gap:5,alignItems:"center",flexShrink:0}}>
          {!task.done && <MonsterBadge days={days} mode={mode}/>}
          {!task.done && !isArchive && <button onClick={()=>onEdit(task)} style={{background:"none",border:"none",color:"#444",fontSize:13,cursor:"pointer",padding:"0 2px"}}>✏️</button>}
          {pending && <span style={{fontSize:9,color:"#444",fontFamily:"'DM Mono', monospace",letterSpacing:0.5,flexShrink:0}}>→ archive in {minsLeft}m</span>}
          {!task.done && !isArchive && (
            confirmDelete ? (
              <span style={{display:"flex",gap:4}}>
                <button onClick={()=>onDelete(task.id)} style={{background:"#FF3333",border:"none",color:"#fff",fontSize:9,padding:"2px 6px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>YES</button>
                <button onClick={()=>setConfirmDelete(false)} style={{background:"#222",border:"none",color:"#777",fontSize:9,padding:"2px 6px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>NO</button>
              </span>
            ) : (
              <button onClick={()=>setConfirmDelete(true)} style={{background:"none",border:"none",color:"#333",fontSize:12,cursor:"pointer",padding:"0 2px"}}>✕</button>
            )
          )}
        </div>
      </div>
      {task.detail && <p style={{margin:"0 0 8px",fontSize:13,color:"#555",lineHeight:1.4}}>{task.detail}</p>}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          {/* Multi-avatar row */}
          <div style={{display:"flex",alignItems:"center",gap:2}}>
            {whoList.map((w,i) => <Avatar key={i} who={w} size={16} collabs={collabs}/>)}
          </div>
          <span style={{fontSize:11,color:task.done?"#555":firstPerson.color,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>
            {whoList.length > 1
              ? whoList.map(w => PEOPLE[w]?.label || collabs.find(c=>c.uid===w)?.name?.split(" ")[0] || w.slice(0,6)).join(" + ")
              : (PEOPLE[firstWho]?.label || firstCollab?.name?.split(" ")[0] || firstWho)}
          </span>
          {task.priority==="HIGH"&&!task.done&&<span style={{fontSize:10,color:"#FF3333",fontWeight:800,letterSpacing:1}}>!! URGENT</span>}
          {dueBadge&&<span style={{fontSize:9,color:dueBadge.color,fontFamily:"'DM Mono', monospace",fontWeight:700,letterSpacing:0.5,border:`1px solid ${dueBadge.color}44`,padding:"1px 5px"}}>{dueBadge.text}</span>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {!task.done && <span style={{fontFamily:"'DM Mono', monospace",fontSize:11,color:mIdx>=3?monster.color:"#333"}}>{days}D</span>}
          {!isArchive && (
            <div onClick={()=>onToggle(task.id)} style={{width:18,height:18,flexShrink:0,cursor:"pointer",border:`2px solid ${task.done?"#444":"#3a3a3a"}`,background:task.done?"#3a3a3a":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {task.done&&<span style={{color:"#888",fontSize:11,fontWeight:900}}>✓</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── HOME VIEW ────────────────────────────────────────────────
function HomeView({ tasks, onToggle, onDelete, onEdit }) {
  const [view, setView] = useState("category");
  const archived = tasks.filter(t=>t.done && !isPendingArchive(t));
  const pendingArchive = tasks.filter(t=>isPendingArchive(t));
  const active = tasks.filter(t=>!t.done);
  const panelTasks = [...active, ...pendingArchive];
  const highCount = active.filter(t=>t.priority==="HIGH").length;

  const Tab = ({val,label}) => (
    <button onClick={()=>setView(val)} style={{padding:"8px 16px",border:"none",cursor:"pointer",background:view===val?"#F0EDE8":"transparent",color:view===val?"#000":"#444",fontSize:11,fontWeight:800,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Archivo Black', sans-serif",transition:"all 0.1s"}}>{label}</button>
  );

  return (
    <>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:0}}>
        {highCount>0&&<div style={{background:"#FF3333",color:"#fff",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>!! {highCount} URGENT{highCount>1?"S":""}</div>}
        <div style={{background:"#1e1e1e",color:"#555",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>{active.length} PENDING</div>
        <div style={{background:"#1e1e1e",color:"#555",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>{archived.length} DONE</div>
      </div>

      <div style={{display:"flex",gap:0,padding:"10px 0",overflowX:"auto",msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {HOME_MONSTERS.map((m,i)=>(
          <div key={i} style={{padding:"2px 7px",marginRight:3,background:m.bg,border:`1px solid ${m.border}`,display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
            <span style={{fontSize:10}}>{m.emoji}</span>
            <span style={{fontSize:9,color:m.color,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",fontFamily:"'Archivo Black', sans-serif"}}>{m.label}</span>
          </div>
        ))}
      </div>

      <div style={{display:"flex",borderTop:"2px solid #1e1e1e",borderBottom:"2px solid #1e1e1e",marginBottom:16}}>
        <Tab val="category" label="Por área"/>
        <Tab val="person"   label="Por persona"/>
        <Tab val="archive"  label={`Archive${archived.length>0?` (${archived.length})`:""}`}/>
      </div>

      {view==="category" && (
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          {Object.entries(HOME_CATEGORIES).map(([key,cat])=>{
            const allCat=tasks.filter(t=>t.cat===key);
            const panelCat=panelTasks.filter(t=>t.cat===key);
            if(!panelCat.length) return null;
            const pct=allCat.length>0?Math.round((allCat.filter(t=>t.done&&!isPendingArchive(t)).length/allCat.length)*100):0;
            const activeCat=panelCat.filter(t=>!t.done);
            const pendingCat=panelCat.filter(t=>isPendingArchive(t));
            return (
              <div key={key}>
                <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:6}}>
                  <div style={{background:cat.color,color:"#000",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1,display:"flex",alignItems:"center",gap:8}}>
                    <span>{cat.emoji}</span><span>{cat.label.toUpperCase()}</span>
                  </div>
                  <div style={{background:"#1a1a1a",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#555",letterSpacing:1}}>{pct}%</div>
                  <div style={{flex:1,borderBottom:"2px solid #1a1a1a"}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  {[...activeCat,...pendingCat].map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit}/>)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view==="person" && (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:20}}>
            {["Ana","Keops"].map(who=>{
              const p=PEOPLE[who];
              const mine=tasks.filter(t=>t.who===who);
              const pending=mine.filter(t=>!t.done).length;
              return (
                <div style={{flex:1,minWidth:0}} key={who}>
                  <div style={{background:p.color,color:"#000",padding:"8px 12px",marginBottom:6,display:"flex",alignItems:"center",gap:10}}>
                    <Avatar who={who} size={20}/>
                    <div>
                      <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:13,letterSpacing:1}}>{who.toUpperCase()}</div>
                      <div style={{fontSize:10,opacity:0.6,fontWeight:600}}>{pending} pending</div>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:3}}>
                    {mine.filter(t=>!t.done||isPendingArchive(t)).sort((a,b)=>a.done-b.done||getDays(b.date)-getDays(a.date)).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit}/>)}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{borderTop:"2px solid #222",paddingTop:16}}>
            <div style={{background:"#C4C4C4",color:"#000",padding:"8px 12px",marginBottom:6,display:"flex",alignItems:"center",gap:10,width:"fit-content"}}>
              <Avatar who="TODOS" size={20}/>
              <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:13,letterSpacing:1}}>★ JUNTOS</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:3}}>
              {tasks.filter(t=>t.who==="TODOS"&&(!t.done||isPendingArchive(t))).sort((a,b)=>a.done-b.done).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit}/>)}
            </div>
          </div>
        </div>
      )}

      {view==="archive" && (
        archived.length===0
          ? <div style={{textAlign:"center",padding:"60px 20px"}}><div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:30,color:"#222",letterSpacing:1}}>NOTHING YET</div><div style={{fontSize:14,color:"#2a2a2a",marginTop:8}}>Completed tasks will appear here.</div></div>
          : <div style={{display:"flex",flexDirection:"column",gap:3}}>{archived.map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} isArchive={true}/>)}</div>
      )}
    </>
  );
}

// ─── MANAGE MEMBERS MODAL ─────────────────────────────────────
function ManageMembersModal({ label, color, members=[], collabs=[], onClose, onSave }) {
  // Always include Ana + Keops as defaults
  const DEFAULT_UIDS = [OWNER_UIDS[0], OWNER_UIDS[1]]; // Keops, Ana
  const initList = () => {
    const base = [...new Set([...DEFAULT_UIDS, ...members])];
    return base;
  };
  const [list, setList] = useState(initList);
  const [showNewForm, setShowNewForm] = useState(false);
  const [uidInput, setUidInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [error, setError] = useState("");

  // All selectable people: owners + existing collabs
  const allPeople = [
    { uid: OWNER_UIDS[0], name: "Keops", color: "#FFD93D" },
    { uid: OWNER_UIDS[1], name: "Ana",   color: ANA_COLOR  },
    ...collabs.map(c => ({ uid: c.uid, name: c.name, color: collabColor(c.uid) }))
  ].filter((p,i,arr) => arr.findIndex(x=>x.uid===p.uid)===i); // dedupe

  const toggle = (uid) => {
    if (DEFAULT_UIDS.includes(uid)) return; // Ana + Keops can't be removed
    setList(l => l.includes(uid) ? l.filter(u=>u!==uid) : [...l, uid]);
  };

  const addNew = () => {
    const uid = uidInput.trim();
    const name = nameInput.trim();
    if (!uid) { setError("UID is required"); return; }
    if (!name) { setError("Name is required"); return; }
    if (list.includes(uid)) { setError("Already added"); return; }
    setList(l => [...l, uid]);
    setUidInput(""); setNameInput(""); setError("");
    setShowNewForm(false);
  };

  const getColor = uid => allPeople.find(p=>p.uid===uid)?.color || collabColor(uid);
  const getName  = uid => allPeople.find(p=>p.uid===uid)?.name || uid.slice(0,10)+"...";

  return (
    <div style={{position:"fixed",inset:0,zIndex:110,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${color}`,padding:"24px 20px 40px",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>👥 MEMBERS — {label}</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Toggle list — all known people */}
          <div>
            <label style={labelStyle}>Team members</label>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {allPeople.map(p => {
                const selected = list.includes(p.uid);
                const isDefault = DEFAULT_UIDS.includes(p.uid);
                return (
                  <div key={p.uid} onClick={()=>toggle(p.uid)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",border:`2px solid ${selected?p.color:"#1a1a1a"}`,borderLeft:`4px solid ${selected?p.color:"#2a2a2a"}`,background:selected?p.color+"11":"transparent",cursor:isDefault?"default":"pointer",transition:"all 0.1s"}}>
                    <div style={{width:28,height:28,background:p.color+"22",border:`1px solid ${p.color}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:10,color:p.color,fontWeight:900}}>{initials(p.name)}</span>
                    </div>
                    <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:13,color:selected?"#F0EDE8":"#555",flex:1,letterSpacing:0.5}}>{p.name}</span>
                    {isDefault && <span style={{fontSize:9,color:"#333",fontFamily:"'DM Mono', monospace",letterSpacing:1}}>DEFAULT</span>}
                    {!isDefault && <div style={{width:18,height:18,border:`2px solid ${selected?p.color:"#2a2a2a"}`,background:selected?p.color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {selected && <span style={{color:"#000",fontSize:11,fontWeight:900}}>✓</span>}
                    </div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add new collaborator — collapsed by default */}
          <div>
            {!showNewForm
              ? <button onClick={()=>setShowNewForm(true)} style={{background:"none",border:"1px solid #2a2a2a",color:"#444",padding:"8px 14px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1,width:"100%"}}>+ ADD NEW COLLABORATOR</button>
              : <div style={{border:"1px solid #2a2a2a",padding:"12px",display:"flex",flexDirection:"column",gap:8}}>
                  <label style={labelStyle}>New collaborator</label>
                  <input value={nameInput} onChange={e=>{setNameInput(e.target.value);setError("");}} placeholder="Full name" style={inputStyle} autoFocus/>
                  <div style={{display:"flex",gap:8}}>
                    <input value={uidInput} onChange={e=>{setUidInput(e.target.value);setError("");}} placeholder="Firebase UID" style={{...inputStyle,flex:1,fontSize:11,fontFamily:"'DM Mono', monospace"}}/>
                    <button onClick={addNew} style={{background:color,border:"none",cursor:"pointer",padding:"0 14px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:11,letterSpacing:1,flexShrink:0}}>ADD</button>
                  </div>
                  {error && <div style={{fontSize:10,color:"#FF3333",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>{error}</div>}
                  <div style={{fontSize:9,color:"#333",fontFamily:"'DM Mono', monospace"}}>Firebase Console → Authentication → Users → User UID</div>
                </div>
            }
          </div>

          <button onClick={()=>onSave(list, allPeople.filter(p=>list.includes(p.uid) && !OWNER_UIDS.includes(p.uid)))}
            style={{width:"100%",padding:"13px",background:color,border:"none",cursor:"pointer",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>
            SAVE MEMBERS
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT CLIENT MODAL ────────────────────────────────────────
function EditClientModal({ client, onClose, onSave }) {
  const [name, setName] = useState(client.name);
  const [color, setColor] = useState(client.color||CLIENT_DEFAULT_COLOR);
  const [links, setLinks] = useState(client.links||[]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const valid = name.trim().length > 0;
  const resolvedColor = hexValid(color) ? color : CLIENT_DEFAULT_COLOR;

  const addLink = () => {
    const url = linkUrl.trim();
    const label = linkLabel.trim();
    if (!url) return;
    const fullUrl = url.startsWith("http") ? url : "https://"+url;
    setLinks(l => [...l, { label: label||fullUrl, url: fullUrl }]);
    setLinkLabel(""); setLinkUrl("");
  };
  const removeLink = (i) => setLinks(l => l.filter((_,idx)=>idx!==i));

  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${client.color||CLIENT_DEFAULT_COLOR}`,padding:"24px 20px 40px",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>✏️ EDIT CLIENT</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Client name *</label><input value={name} onChange={e=>setName(e.target.value)} style={inputStyle} autoFocus/></div>
          <div>
            <label style={labelStyle}>Brand color</label>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input value={color} onChange={e=>setColor(e.target.value)} placeholder="#6BCEFF" style={{...inputStyle,width:"auto",flex:1}}/>
              <div style={{width:36,height:36,background:resolvedColor,flexShrink:0,border:"2px solid #2a2a2a"}}/>
            </div>
          </div>

          {/* Links section */}
          <div>
            <label style={labelStyle}>Links & Resources</label>
            {links.length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:8}}>
                {links.map((lnk,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",border:"1px solid #2a2a2a",borderLeft:`3px solid ${resolvedColor}`}}>
                    <span style={{fontSize:12,color:"#F0EDE8",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lnk.label}</span>
                    <button onClick={()=>removeLink(i)} style={{background:"none",border:"none",color:"#FF3333",cursor:"pointer",fontSize:11,flexShrink:0}}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <input value={linkLabel} onChange={e=>setLinkLabel(e.target.value)} placeholder="Label (e.g. Dropbox Assets)" style={{...inputStyle,fontSize:13}}/>
              <div style={{display:"flex",gap:8}}>
                <input value={linkUrl} onChange={e=>setLinkUrl(e.target.value)} placeholder="https://..." style={{...inputStyle,flex:1,fontSize:13,fontFamily:"'DM Mono', monospace"}} onKeyDown={e=>e.key==="Enter"&&addLink()}/>
                <button onClick={addLink} disabled={!linkUrl.trim()} style={{background:linkUrl.trim()?resolvedColor:"#1a1a1a",border:"none",cursor:linkUrl.trim()?"pointer":"default",padding:"0 14px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:11,letterSpacing:1,flexShrink:0}}>+ ADD</button>
              </div>
            </div>
          </div>

          <button onClick={()=>valid&&onSave({name:name.trim(),color:resolvedColor,links})} style={{width:"100%",padding:"13px",background:valid?resolvedColor:"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>SAVE CHANGES</button>
          <div style={{borderTop:"1px solid #1a1a1a",paddingTop:12}}>
            {confirmClose ? (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:11,color:"#555",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,flex:1}}>CLOSE THIS CLIENT?</span>
                <button onClick={()=>onSave({closed:true})} style={{background:"#FF3333",border:"none",color:"#fff",padding:"6px 12px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>YES, CLOSE</button>
                <button onClick={()=>setConfirmClose(false)} style={{background:"#1a1a1a",border:"none",color:"#555",padding:"6px 12px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>CANCEL</button>
              </div>
            ) : (
              <button onClick={()=>setConfirmClose(true)} style={{background:"none",border:"1px solid #2a2a2a",color:"#333",padding:"6px 14px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1,width:"100%"}}>CLOSE CLIENT</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT PROJECT MODAL ───────────────────────────────────────
function EditProjectModal({ project, onClose, onSave }) {
  const [name, setName] = useState(project.name);
  const [desc, setDesc] = useState(project.desc||"");
  const [confirmClose, setConfirmClose] = useState(false);
  const valid = name.trim().length > 0;
  return (
    <div style={{position:"fixed",inset:0,zIndex:100,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${AKZ_ACCENT}`,padding:"24px 20px 40px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>✏️ EDIT PROJECT</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div><label style={labelStyle}>Project name *</label><input value={name} onChange={e=>setName(e.target.value)} style={inputStyle} autoFocus/></div>
          <div><label style={labelStyle}>Description</label><input value={desc} onChange={e=>setDesc(e.target.value)} style={inputStyle}/></div>
          <button onClick={()=>valid&&onSave({name:name.trim(),desc:desc.trim()})} style={{width:"100%",padding:"13px",background:valid?AKZ_ACCENT:"#1a1a1a",border:"none",cursor:valid?"pointer":"default",color:valid?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>SAVE CHANGES</button>
          <div style={{borderTop:"1px solid #1a1a1a",paddingTop:12}}>
            {confirmClose ? (
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:11,color:"#555",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,flex:1}}>CLOSE THIS PROJECT?</span>
                <button onClick={()=>onSave({closed:true})} style={{background:"#FF3333",border:"none",color:"#fff",padding:"6px 12px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>YES, CLOSE</button>
                <button onClick={()=>setConfirmClose(false)} style={{background:"#1a1a1a",border:"none",color:"#555",padding:"6px 12px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>CANCEL</button>
              </div>
            ) : (
              <button onClick={()=>setConfirmClose(true)} style={{background:"none",border:"1px solid #2a2a2a",color:"#333",padding:"6px 14px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1,width:"100%"}}>CLOSE PROJECT</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── IMPORT MODAL ─────────────────────────────────────────────
function ImportModal({ onClose, onImport, clients, projects, collabs=[] }) {
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState(null);
  const [whoOverride, setWhoOverride] = useState("Ana");
  const [targetClientId, setTargetClientId] = useState("auto"); // "auto" | clientId | "action"

  const parse = () => {
    const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);
    const groups = [];
    let current = null;
    for (const line of lines) {
      const isTask = /^[•\-\[\]✓*]/.test(line) || /^\[[ x]\]/.test(line);
      if (!isTask && line.length > 0 && line.length < 60 && !line.startsWith("•")) {
        current = { header: line, tasks: [] };
        groups.push(current);
      } else if (current && isTask) {
        const text = line.replace(/^[•\-\[\]\s✓*]+/, "").replace(/^\[ \]\s*/, "").trim();
        if (text) current.tasks.push(text);
      } else if (!current && isTask) {
        if (!groups.length || groups[groups.length-1].header !== "General") {
          current = { header: "General", tasks: [] };
          groups.push(current);
        }
        const text = line.replace(/^[•\-\[\]\s✓*]+/, "").trim();
        if (text) current.tasks.push(text);
      }
    }
    setParsed(groups.filter(g => g.tasks.length > 0));
  };

  const resolveClient = (header) => {
    if (targetClientId !== "auto") return clients.find(c => c.id === targetClientId) || null;
    const h = header.toLowerCase();
    return clients.find(c => h.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(h));
  };
  const resolveProject = (header) => {
    if (targetClientId !== "auto") return null;
    const h = header.toLowerCase();
    return projects.find(p => h.includes(p.name.toLowerCase()) || p.name.toLowerCase().includes(h));
  };

  const totalTasks = parsed ? parsed.reduce((acc, g) => acc + g.tasks.length, 0) : 0;
  const activeClients = clients.filter(c => !c.closed);

  return (
    <div style={{position:"fixed",inset:0,zIndex:110,background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"flex-end"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,margin:"0 auto",background:"#0f0e0c",borderTop:`3px solid ${AKZ_ACCENT}`,padding:"24px 20px 40px",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:18,letterSpacing:1,color:"#F0EDE8"}}>📋 IMPORT TASKS</span>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#555",fontSize:20,cursor:"pointer"}}>✕</button>
        </div>

        {!parsed ? (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div>
              <label style={labelStyle}>Paste your list</label>
              <div style={{fontSize:10,color:"#444",fontFamily:"'DM Mono', monospace",marginBottom:8}}>Headers = client/project names · Bullets = tasks</div>
              <textarea
                value={raw}
                onChange={e=>setRaw(e.target.value)}
                placeholder={"Drafter\n• Consultar con Manlio POP\n• Propuesta económica\n\nOrazio\n• Menú barra\n• Crear instagram"}
                autoFocus
                style={{...inputStyle,height:200,resize:"vertical",lineHeight:1.6,fontSize:13,fontFamily:"'DM Mono', monospace"}}
              />
            </div>
            <div>
              <label style={labelStyle}>Assign to</label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["Ana","Keops","TODOS"].map(w => (
                  <button key={w} onClick={()=>setWhoOverride(w)}
                    style={{padding:"5px 12px",border:`2px solid ${whoOverride===w?(w==="Ana"?ANA_COLOR:w==="Keops"?"#FFD93D":"#C4C4C4"):"#2a2a2a"}`,background:whoOverride===w?(w==="Ana"?ANA_COLOR:w==="Keops"?"#FFD93D":"#C4C4C4")+"22":"transparent",color:whoOverride===w?(w==="Ana"?ANA_COLOR:w==="Keops"?"#FFD93D":"#C4C4C4"):"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>
                    {w==="TODOS"?"Juntos":w}
                  </button>
                ))}
                {collabs.map(c => {
                  const col = collabColor(c.uid);
                  return (
                    <button key={c.uid} onClick={()=>setWhoOverride(c.uid)}
                      style={{padding:"5px 12px",border:`2px solid ${whoOverride===c.uid?col:"#2a2a2a"}`,background:whoOverride===c.uid?col+"22":"transparent",color:whoOverride===c.uid?col:"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>
                      {c.name.split(" ")[0]}
                    </button>
                  );
                })}
              </div>
            </div>
            <button onClick={parse} disabled={!raw.trim()}
              style={{width:"100%",padding:"13px",background:raw.trim()?AKZ_ACCENT:"#1a1a1a",border:"none",cursor:raw.trim()?"pointer":"default",color:raw.trim()?"#000":"#333",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase"}}>
              PARSE LIST
            </button>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <div style={{background:AKZ_ACCENT,color:"#000",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:11,letterSpacing:1}}>{totalTasks} TASKS FOUND</div>
              <button onClick={()=>setParsed(null)} style={{background:"none",border:"1px solid #2a2a2a",color:"#555",padding:"3px 10px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1}}>← EDIT</button>
            </div>

            {/* Client selector */}
            <div>
              <label style={labelStyle}>Add to client <span style={{color:"#333",fontWeight:400,letterSpacing:0,textTransform:"none",fontFamily:"'Inter', sans-serif"}}>— override auto-detect</span></label>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>setTargetClientId("auto")}
                  style={{padding:"5px 10px",border:`2px solid ${targetClientId==="auto"?"#F0EDE8":"#2a2a2a"}`,background:targetClientId==="auto"?"#F0EDE8"+"22":"transparent",color:targetClientId==="auto"?"#F0EDE8":"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif"}}>
                  AUTO
                </button>
                <button onClick={()=>setTargetClientId("action")}
                  style={{padding:"5px 10px",border:`2px solid ${targetClientId==="action"?"#FFD93D":"#2a2a2a"}`,background:targetClientId==="action"?"#FFD93D22":"transparent",color:targetClientId==="action"?"#FFD93D":"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif"}}>
                  ⚡ ACTIONS
                </button>
                {activeClients.map(c => (
                  <button key={c.id} onClick={()=>setTargetClientId(c.id)}
                    style={{padding:"5px 10px",border:`2px solid ${targetClientId===c.id?(c.color||CLIENT_DEFAULT_COLOR):"#2a2a2a"}`,background:targetClientId===c.id?(c.color||CLIENT_DEFAULT_COLOR)+"22":"transparent",color:targetClientId===c.id?(c.color||CLIENT_DEFAULT_COLOR):"#555",fontSize:10,fontWeight:700,letterSpacing:1,cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",textTransform:"uppercase"}}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {parsed.map((group, gi) => {
              const client = resolveClient(group.header);
              const project = !client ? resolveProject(group.header) : null;
              const matched = client || project;
              const forcedClient = targetClientId !== "auto" && targetClientId !== "action" ? clients.find(c=>c.id===targetClientId) : null;
              const displayColor = forcedClient ? (forcedClient.color||CLIENT_DEFAULT_COLOR) : matched ? (client?client.color||CLIENT_DEFAULT_COLOR:AKZ_ACCENT) : "#2a2a2a";
              return (
                <div key={gi} style={{border:`2px solid ${displayColor}`,padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1,color:displayColor==="2a2a2a"?"#F0EDE8":displayColor}}>
                      {forcedClient?"👤":client?"👤":project?"🔧":"⚡"} {group.header.toUpperCase()}
                    </div>
                    {forcedClient
                      ? <div style={{fontSize:9,color:forcedClient.color||CLIENT_DEFAULT_COLOR,fontFamily:"'DM Mono', monospace",border:`1px solid ${forcedClient.color||CLIENT_DEFAULT_COLOR}44`,padding:"1px 6px"}}>→ {forcedClient.name}</div>
                      : targetClientId==="action"
                        ? <div style={{fontSize:9,color:"#FFD93D",fontFamily:"'DM Mono', monospace",border:"1px solid #FFD93D44",padding:"1px 6px"}}>→ action</div>
                        : matched
                          ? <div style={{fontSize:9,color:"#555",fontFamily:"'DM Mono', monospace",border:"1px solid #2a2a2a",padding:"1px 6px"}}>matched</div>
                          : <div style={{fontSize:9,color:"#FF8C42",fontFamily:"'DM Mono', monospace",border:"1px solid #FF8C4244",padding:"1px 6px"}}>→ action</div>
                    }
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {group.tasks.map((t, ti) => (
                      <div key={ti} style={{display:"flex",alignItems:"flex-start",gap:8,padding:"6px 0",borderTop:"1px solid #1a1a1a"}}>
                        <span style={{color:AKZ_ACCENT,fontSize:10,marginTop:2,flexShrink:0}}>▸</span>
                        <span style={{fontSize:13,color:"#F0EDE8",lineHeight:1.4}}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => { onImport(parsed, whoOverride, targetClientId); onClose(); }}
              style={{width:"100%",padding:"13px",background:AKZ_ACCENT,border:"none",cursor:"pointer",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:2,textTransform:"uppercase",marginTop:4}}>
              CREATE {totalTasks} TASKS
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── URGENCY SORT ─────────────────────────────────────────────
function urgencyScore(task) {
  const mIdx = getAKZMonsterIdx(task);
  const priorityBoost = task.priority==="HIGH" ? 10 : task.priority==="MID" ? 5 : 0;
  return mIdx * 10 + priorityBoost;
}

// ─── COLLAPSIBLE SECTION ──────────────────────────────────────
function CollapsibleSection({ label, color, count, onAdd, addLabel, onEdit_, onMembers, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:open?6:0}}>
        <div onClick={()=>setOpen(o=>!o)} style={{background:color,color:"#000",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1,cursor:"pointer",userSelect:"none",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:9,display:"inline-block",transform:open?"rotate(0deg)":"rotate(-90deg)",transition:"transform 0.15s"}}>▼</span>
          <span>{label}</span>
        </div>
        <div style={{background:"#1a1a1a",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#555",letterSpacing:1}}>{count}</div>
        <div style={{flex:1,borderBottom:"2px solid #1a1a1a"}}/>
        {onAdd && <button onClick={onAdd} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 10px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:6}}>{addLabel||"+ ADD"}</button>}
        {onEdit_ && <button onClick={onEdit_} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:4}}>✏️</button>}
        {onMembers && <button onClick={onMembers} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:4}}>👥</button>}
      </div>
      {open && children}
    </div>
  );
}

// ─── AKZ VIEW ─────────────────────────────────────────────────
function AKZView({ tasks, clients, projects, collabs=[], onToggle, onDelete, onEdit, onAddClient, onAddProject, onAddTask, onEditClient, onEditProject, onManageClientMembers, onManageProjectMembers, onImport, isOwner=true }) {
  const [showImport, setShowImport] = useState(false);
  const [view, setView] = useState("general");
  const done = tasks.filter(t=>t.done && !isPendingArchive(t));
  const active = tasks.filter(t=>!t.done);
  const highCount = active.filter(t=>t.priority==="HIGH").length;

  const Tab = ({val,label}) => (
    <button onClick={()=>setView(val)} style={{padding:"8px 12px",border:"none",cursor:"pointer",background:view===val?"#F0EDE8":"transparent",color:view===val?"#000":"#444",fontSize:10,fontWeight:800,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Archivo Black', sans-serif",transition:"all 0.1s",whiteSpace:"nowrap"}}>{label}</button>
  );

  const actions = active.filter(t=>t.type==="action"||!t.type);
  const clientTasks = active.filter(t=>t.type==="client");
  const projectTasks = active.filter(t=>t.type==="project");
  const activeClients = clients.filter(c=>!c.closed);
  const closedClients = clients.filter(c=>c.closed);
  const activeProjects = projects.filter(p=>!p.closed);
  const closedProjects = projects.filter(p=>p.closed);
  const sortByUrgency = arr => [...arr].sort((a,b)=>urgencyScore(b)-urgencyScore(a));

  return (
    <>
      {showImport && <ImportModal onClose={()=>setShowImport(false)} onImport={(groups,who,targetClientId)=>{onImport(groups,who,targetClientId);setShowImport(false);}} clients={clients} projects={projects} collabs={collabs}/>}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:0}}>
        {highCount>0&&<div style={{background:"#FF3333",color:"#fff",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>!! {highCount} URGENT{highCount>1?"S":""}</div>}
        <div style={{background:"#1e1e1e",color:"#555",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>{active.length} OPEN</div>
        <div style={{background:"#1e1e1e",color:"#555",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>{done.length} CLOSED</div>
        <div style={{background:"#1e1e1e",color:"#555",padding:"3px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>{clients.length} CLIENTS</div>
        {isOwner&&<button onClick={()=>setShowImport(true)} style={{background:"none",border:"1px solid #2a2a2a",color:"#555",padding:"3px 10px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1,marginLeft:"auto"}}>📋 IMPORT</button>}
      </div>

      <div style={{display:"flex",gap:0,padding:"10px 0",overflowX:"auto",msOverflowStyle:"none",scrollbarWidth:"none"}}>
        {AKZ_MONSTERS.map((m,i)=>(
          <div key={i} style={{padding:"2px 7px",marginRight:3,background:m.bg,border:`1px solid ${m.border}`,display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
            <span style={{fontSize:10}}>{m.emoji}</span>
            <span style={{fontSize:9,color:m.color,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",fontFamily:"'Archivo Black', sans-serif"}}>{m.label}</span>
          </div>
        ))}
      </div>

      <div style={{display:"flex",borderTop:"2px solid #1e1e1e",borderBottom:"2px solid #1e1e1e",marginBottom:16,overflowX:"auto"}}>
        <Tab val="general"  label="⚡ General"/>
        <Tab val="clients"  label={`👤 Clients${activeClients.length>0?` (${activeClients.length})`:""}`}/>
        <Tab val="projects" label={`🔧 Projects${activeProjects.length>0?` (${activeProjects.length})`:""}`}/>
        <Tab val="manage"   label="⚙️ Manage"/>
        <Tab val="archive"  label={`Archive${done.length>0?` (${done.length})`:""}`}/>
      </div>

      {view==="general" && (
        <div>
          <CollapsibleSection label="⚡ ACTIONS" color={AKZ_ACCENT} count={`${actions.length} open`} onAdd={()=>onAddTask("action")} addLabel="+ ADD">
            {actions.length===0
              ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:4}}>NO OPEN ACTIONS</div>
              : <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:4}}>{sortByUrgency(actions).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" collabs={collabs}/>)}</div>
            }
          </CollapsibleSection>

          {activeClients.map(client=>{
            const cTasks = clientTasks.filter(t=>t.clientId===client.id);
            const color = client.color||CLIENT_DEFAULT_COLOR;
            const allCT = tasks.filter(t=>t.clientId===client.id);
            const pct = allCT.length>0 ? Math.round((allCT.filter(t=>t.done).length/allCT.length)*100) : 0;
            return (
              <CollapsibleSection key={client.id} label={`👤 ${client.name.toUpperCase()}`} color={color} count={`${cTasks.length} open · ${pct}%`} onAdd={isOwner?()=>onAddTask("client",client.id):null} addLabel="+ TASK" onEdit_={isOwner?()=>onEditClient(client):null} onMembers={isOwner?()=>onManageClientMembers(client):null}>
                {client.links&&client.links.length>0&&(
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8,paddingLeft:2}}>
                    {client.links.map((lnk,i)=>(
                      <a key={i} href={lnk.url} target="_blank" rel="noopener noreferrer"
                        style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 8px",border:`1px solid ${color}44`,color:color,fontSize:10,fontFamily:"'DM Mono', monospace",textDecoration:"none",letterSpacing:0.5,background:color+"11"}}>
                        🔗 {lnk.label}
                      </a>
                    ))}
                  </div>
                )}
                {cTasks.length===0
                  ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:4}}>NO OPEN TASKS</div>
                  : <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:4}}>{sortByUrgency(cTasks).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" collabs={collabs}/>)}</div>
                }
              </CollapsibleSection>
            );
          })}

          {activeProjects.map(proj=>{
            const pTasks = projectTasks.filter(t=>t.projectId===proj.id);
            const allPT = tasks.filter(t=>t.projectId===proj.id);
            const pct = allPT.length>0 ? Math.round((allPT.filter(t=>t.done).length/allPT.length)*100) : 0;
            return (
              <CollapsibleSection key={proj.id} label={`🔧 ${proj.name.toUpperCase()}`} color="#333" count={`${pTasks.length} open · ${pct}%`} onAdd={isOwner?()=>onAddTask("project",null,proj.id):null} addLabel="+ TASK" onEdit_={isOwner?()=>onEditProject(proj):null} onMembers={isOwner?()=>onManageProjectMembers(proj):null}>
                {proj.desc&&<div style={{fontSize:12,color:"#444",marginBottom:6,paddingLeft:2}}>{proj.desc}</div>}
                {pTasks.length===0
                  ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:4}}>NO OPEN TASKS</div>
                  : <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:4}}>{sortByUrgency(pTasks).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" collabs={collabs}/>)}</div>
                }
              </CollapsibleSection>
            );
          })}

          {active.length===0&&<div style={{textAlign:"center",padding:"60px 20px",color:"#2a2a2a",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:1}}>TODO LIMPIO 🖤</div>}
        </div>
      )}

      {view==="clients" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1}}>{activeClients.length} CLIENT{activeClients.length!==1?"S":""} ACTIVE</div>
            <button onClick={onAddClient} style={{background:CLIENT_DEFAULT_COLOR,border:"none",cursor:"pointer",padding:"4px 10px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>+ NEW CLIENT</button>
          </div>
          {activeClients.length===0 && closedClients.length===0
            ? <div style={{textAlign:"center",padding:"40px 20px",color:"#2a2a2a",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:1}}>NO CLIENTS YET</div>
            : <>
              {activeClients.map(client=>{
                const cTasks = clientTasks.filter(t=>t.clientId===client.id);
                const color = client.color||CLIENT_DEFAULT_COLOR;
                const allCT = tasks.filter(t=>t.clientId===client.id);
                const pct = allCT.length>0 ? Math.round((allCT.filter(t=>t.done).length/allCT.length)*100) : 0;
                return (
                  <div key={client.id} style={{marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:6}}>
                      <div style={{background:color,color:"#000",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>👤 {client.name.toUpperCase()}</div>
                      <div style={{background:"#1a1a1a",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#555",letterSpacing:1}}>{pct}%</div>
                      <div style={{flex:1,borderBottom:"2px solid #1a1a1a"}}/>
                      <button onClick={()=>onAddTask("client",client.id)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:6}}>+ TASK</button>
                      <button onClick={()=>onEditClient(client)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:4}}>✏️</button>
                    </div>
                    {cTasks.length===0
                      ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>NO OPEN TASKS</div>
                      : <div style={{display:"flex",flexDirection:"column",gap:3}}>{sortByUrgency(cTasks).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" collabs={collabs}/>)}</div>
                    }
                  </div>
                );
              })}
              {closedClients.length>0&&<div style={{borderTop:"1px solid #1a1a1a",paddingTop:12,marginTop:4}}>
                <div style={{fontSize:10,color:"#333",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:10}}>CLOSED — {closedClients.length}</div>
                {closedClients.map(c=>(
                  <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",border:"1px solid #1a1a1a",marginBottom:4,opacity:0.5}}>
                    <div style={{width:10,height:10,background:c.color||CLIENT_DEFAULT_COLOR,flexShrink:0}}/>
                    <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1,flex:1}}>{c.name.toUpperCase()}</span>
                    <button onClick={()=>onEditClient(c)} style={{background:"none",border:"none",color:"#333",cursor:"pointer",fontSize:10,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>REOPEN</button>
                  </div>
                ))}
              </div>}
            </>
          }
        </div>
      )}

      {view==="projects" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1}}>{activeProjects.length} PROJECT{activeProjects.length!==1?"S":""} ACTIVE</div>
            <button onClick={onAddProject} style={{background:AKZ_ACCENT,border:"none",cursor:"pointer",padding:"4px 10px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>+ NEW PROJECT</button>
          </div>
          {activeProjects.length===0 && closedProjects.length===0
            ? <div style={{textAlign:"center",padding:"40px 20px",color:"#2a2a2a",fontFamily:"'Archivo Black', sans-serif",fontSize:14,letterSpacing:1}}>NO PROJECTS YET</div>
            : <>
              {activeProjects.map(proj=>{
                const pTasks = projectTasks.filter(t=>t.projectId===proj.id);
                const allPT = tasks.filter(t=>t.projectId===proj.id);
                const pct = allPT.length>0 ? Math.round((allPT.filter(t=>t.done).length/allPT.length)*100) : 0;
                return (
                  <div key={proj.id} style={{marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:6}}>
                      <div style={{background:AKZ_ACCENT,color:"#000",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,letterSpacing:1}}>🔧 {proj.name.toUpperCase()}</div>
                      <div style={{background:"#1a1a1a",padding:"4px 10px",fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#555",letterSpacing:1}}>{pct}%</div>
                      <div style={{flex:1,borderBottom:"2px solid #1a1a1a"}}/>
                      <button onClick={()=>onAddTask("project",null,proj.id)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:6}}>+ TASK</button>
                      <button onClick={()=>onEditProject(proj)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1,marginLeft:4}}>✏️</button>
                    </div>
                    {proj.desc&&<div style={{fontSize:12,color:"#444",marginBottom:8,paddingLeft:2}}>{proj.desc}</div>}
                    {pTasks.length===0
                      ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>NO OPEN TASKS</div>
                      : <div style={{display:"flex",flexDirection:"column",gap:3}}>{sortByUrgency(pTasks).map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" collabs={collabs}/>)}</div>
                    }
                  </div>
                );
              })}
              {closedProjects.length>0&&<div style={{borderTop:"1px solid #1a1a1a",paddingTop:12,marginTop:4}}>
                <div style={{fontSize:10,color:"#333",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:10}}>CLOSED — {closedProjects.length}</div>
                {closedProjects.map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",border:"1px solid #1a1a1a",marginBottom:4,opacity:0.5}}>
                    <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1,flex:1}}>{p.name.toUpperCase()}</span>
                    <button onClick={()=>onEditProject(p)} style={{background:"none",border:"none",color:"#333",cursor:"pointer",fontSize:10,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>REOPEN</button>
                  </div>
                ))}
              </div>}
            </>
          }
        </div>
      )}

      {view==="manage" && (
        <div>
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1}}>👤 CLIENTS — {activeClients.length} ACTIVE</div>
              <button onClick={onAddClient} style={{background:CLIENT_DEFAULT_COLOR,border:"none",cursor:"pointer",padding:"4px 10px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>+ NEW CLIENT</button>
            </div>
            {activeClients.length===0
              ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>NO ACTIVE CLIENTS</div>
              : activeClients.map(c=>(
                  <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",border:"2px solid #1a1a1a",marginBottom:4}}>
                    <div style={{width:12,height:12,background:c.color||CLIENT_DEFAULT_COLOR,flexShrink:0}}/>
                    <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#F0EDE8",letterSpacing:0.5,flex:1}}>{c.name}</span>
                    <span style={{fontSize:10,color:"#444",fontFamily:"'DM Mono', monospace"}}>{clientTasks.filter(t=>t.clientId===c.id).length} open</span>
                    <button onClick={()=>onEditClient(c)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1}}>✏️ EDIT</button>
                  </div>
                ))
            }
            {closedClients.length>0&&<div style={{marginTop:10}}>
              <div style={{fontSize:10,color:"#333",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:6}}>CLOSED — {closedClients.length}</div>
              {closedClients.map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",border:"1px solid #1a1a1a",marginBottom:4,opacity:0.4}}>
                  <div style={{width:10,height:10,background:c.color||CLIENT_DEFAULT_COLOR,flexShrink:0}}/>
                  <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1,flex:1}}>{c.name.toUpperCase()}</span>
                  <button onClick={()=>onEditClient(c)} style={{background:"none",border:"none",color:"#333",cursor:"pointer",fontSize:10,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>REOPEN</button>
                </div>
              ))}
            </div>}
          </div>

          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1}}>🔧 PROJECTS — {activeProjects.length} ACTIVE</div>
              <button onClick={onAddProject} style={{background:AKZ_ACCENT,border:"none",cursor:"pointer",padding:"4px 10px",color:"#000",fontFamily:"'Archivo Black', sans-serif",fontSize:10,letterSpacing:1}}>+ NEW PROJECT</button>
            </div>
            {activeProjects.length===0
              ? <div style={{padding:"10px 14px",border:"2px solid #1a1a1a",color:"#333",fontSize:12,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>NO ACTIVE PROJECTS</div>
              : activeProjects.map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",border:"2px solid #1a1a1a",marginBottom:4}}>
                    <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:12,color:"#F0EDE8",letterSpacing:0.5,flex:1}}>{p.name}</span>
                    {p.desc&&<span style={{fontSize:11,color:"#444",flex:2}}>{p.desc}</span>}
                    <span style={{fontSize:10,color:"#444",fontFamily:"'DM Mono', monospace"}}>{projectTasks.filter(t=>t.projectId===p.id).length} open</span>
                    <button onClick={()=>onEditProject(p)} style={{background:"none",border:"1px solid #2a2a2a",cursor:"pointer",padding:"2px 8px",color:"#555",fontFamily:"'Archivo Black', sans-serif",fontSize:9,letterSpacing:1}}>✏️ EDIT</button>
                  </div>
                ))
            }
            {closedProjects.length>0&&<div style={{marginTop:10}}>
              <div style={{fontSize:10,color:"#333",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,marginBottom:6}}>CLOSED — {closedProjects.length}</div>
              {closedProjects.map(p=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",border:"1px solid #1a1a1a",marginBottom:4,opacity:0.4}}>
                  <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:11,color:"#555",letterSpacing:1,flex:1}}>{p.name.toUpperCase()}</span>
                  <button onClick={()=>onEditProject(p)} style={{background:"none",border:"none",color:"#333",cursor:"pointer",fontSize:10,fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>REOPEN</button>
                </div>
              ))}
            </div>}
          </div>
        </div>
      )}

      {view==="archive" && (
        done.length===0
          ? <div style={{textAlign:"center",padding:"60px 20px"}}><div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:30,color:"#222",letterSpacing:1}}>NOTHING CLOSED</div><div style={{fontSize:14,color:"#2a2a2a",marginTop:8}}>Closed tasks will appear here.</div></div>
          : <div style={{display:"flex",flexDirection:"column",gap:3}}>{done.map(t=><TaskCard key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} mode="akz" isArchive={true} collabs={collabs}/>)}</div>
      )}
    </>
  );
}

// ─── APP ──────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]             = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [mode, setMode]             = useState("home");

  const [homeTasks, setHomeTasks]   = useState([]);
  const [akzTasks, setAkzTasks]     = useState([]);
  const [clients, setClients]       = useState([]);
  const [projects, setProjects]     = useState([]);
  const [collabs, setCollabs]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);

  const [showHomeAdd, setShowHomeAdd]         = useState(false);
  const [editHomeTask, setEditHomeTask]       = useState(null);
  const [showAkzAdd, setShowAkzAdd]           = useState(false);
  const [akzAddType, setAkzAddType]           = useState("action");
  const [akzAddClientId, setAkzAddClientId]   = useState("");
  const [akzAddProjectId, setAkzAddProjectId] = useState("");
  const [editAkzTask, setEditAkzTask]         = useState(null);
  const [showNewClient, setShowNewClient]     = useState(false);
  const [showNewProject, setShowNewProject]   = useState(false);
  const [editClient, setEditClient]           = useState(null);
  const [editProject, setEditProject]         = useState(null);
  const [manageMembersClient, setManageMembersClient]   = useState(null);
  const [manageMembersProject, setManageMembersProject] = useState(null);

  // ─── Firebase Auth listener ───────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // ─── Firestore listeners (only when authenticated) ────────
  useEffect(() => {
    if (!user) return;
    seedIfEmpty();
    let loaded = 0;
    const check = () => { loaded++; if (loaded>=5) setLoading(false); };
    const u1 = onSnapshot(collection(db,"tasks"),        s=>{ setHomeTasks(s.docs.map(d=>({id:d.id,...d.data()}))); check(); });
    const u2 = onSnapshot(collection(db,"akz_tasks"),    s=>{ setAkzTasks(s.docs.map(d=>({id:d.id,...d.data()}))); check(); });
    const u3 = onSnapshot(collection(db,"akz_clients"),  s=>{ setClients(s.docs.map(d=>({id:d.id,...d.data()}))); check(); });
    const u4 = onSnapshot(collection(db,"akz_projects"), s=>{ setProjects(s.docs.map(d=>({id:d.id,...d.data()}))); check(); });
    const u5 = onSnapshot(collection(db,"akz_collabs"),  s=>{ setCollabs(s.docs.map(d=>({id:d.id,...d.data()}))); check(); });
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, [user]);

  // ─── HOME handlers ────────────────────────────────────────
  const toggleHome = async id => { const t=homeTasks.find(t=>t.id===id); if(t) await updateDoc(doc(db,"tasks",id),{done:!t.done, doneAt: !t.done ? Date.now() : null}); };
  const addHome    = async form => { await addDoc(collection(db,"tasks"),{...form,task:form.task.trim(),detail:form.detail.trim(),done:false}); setShowHomeAdd(false); };
  const saveHome   = async form => { await updateDoc(doc(db,"tasks",editHomeTask.id),{task:form.task.trim(),detail:form.detail.trim(),who:form.who,priority:form.priority,cat:form.cat,date:form.date}); setEditHomeTask(null); };
  const deleteHome = async id   => { await deleteDoc(doc(db,"tasks",id)); };

  // ─── AKZ handlers ─────────────────────────────────────────
  const toggleAkz = async id => { const t=akzTasks.find(t=>t.id===id); if(t) await updateDoc(doc(db,"akz_tasks",id),{done:!t.done, doneAt: !t.done ? Date.now() : null}); };
  const addAkz    = async form => { await addDoc(collection(db,"akz_tasks"),{...form,task:form.task.trim(),detail:form.detail.trim(),who:whoArr(form.who),done:false}); setShowAkzAdd(false); };
  const saveAkz   = async form => { await updateDoc(doc(db,"akz_tasks",editAkzTask.id),{task:form.task.trim(),detail:form.detail.trim(),who:whoArr(form.who),priority:form.priority,date:form.date,dueDate:form.dueDate||"",type:form.type,clientId:form.clientId||"",projectId:form.projectId||""}); setEditAkzTask(null); };
  const deleteAkz = async id   => { await deleteDoc(doc(db,"akz_tasks",id)); };
  const addClient  = async data => { await addDoc(collection(db,"akz_clients"),data); setShowNewClient(false); };
  const addProject = async data => { await addDoc(collection(db,"akz_projects"),data); setShowNewProject(false); };
  const saveClient = async data => {
    if(!editClient) return;
    await updateDoc(doc(db,"akz_clients",editClient.id),data);
    if(data.closed) {
      const openTasks = akzTasks.filter(t=>t.clientId===editClient.id && !t.done);
      await Promise.all(openTasks.map(t=>updateDoc(doc(db,"akz_tasks",t.id),{done:true,doneAt:Date.now()})));
    }
    setEditClient(null);
  };
  const saveProject = async data => {
    if(!editProject) return;
    await updateDoc(doc(db,"akz_projects",editProject.id),data);
    if(data.closed) {
      const openTasks = akzTasks.filter(t=>t.projectId===editProject.id && !t.done);
      await Promise.all(openTasks.map(t=>updateDoc(doc(db,"akz_tasks",t.id),{done:true,doneAt:Date.now()})));
    }
    setEditProject(null);
  };

  const syncCollabs = async (newCollabsList) => {
    // newCollabsList = [{uid, name}, ...] — sync to akz_collabs
    for (const c of newCollabsList) {
      const existing = collabs.find(x => x.uid === c.uid);
      if (!existing) {
        await addDoc(collection(db,"akz_collabs"), { uid: c.uid, name: c.name });
      }
    }
  };
  const saveClientMembers = async (members, newCollabsList=[]) => {
    if (!manageMembersClient) return;
    await updateDoc(doc(db,"akz_clients",manageMembersClient.id),{members});
    await syncCollabs(newCollabsList);
    setManageMembersClient(null);
  };
  const saveProjectMembers = async (members, newCollabsList=[]) => {
    if (!manageMembersProject) return;
    await updateDoc(doc(db,"akz_projects",manageMembersProject.id),{members});
    await syncCollabs(newCollabsList);
    setManageMembersProject(null);
  };

  const handleAddAkzTask = (type, clientId="", projectId="") => {
    setAkzAddType(type); setAkzAddClientId(clientId); setAkzAddProjectId(projectId); setShowAkzAdd(true);
  };

  const handleImport = async (groups, who, targetClientId="auto") => {
    const resolveClient = (header) => {
      if (targetClientId !== "auto" && targetClientId !== "action") return clients.find(c => c.id === targetClientId) || null;
      const h = header.toLowerCase();
      return clients.find(c => h.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(h));
    };
    const resolveProject = (header) => {
      if (targetClientId !== "auto") return null;
      const h = header.toLowerCase();
      return projects.find(p => h.includes(p.name.toLowerCase()) || p.name.toLowerCase().includes(h));
    };
    for (const group of groups) {
      const client = resolveClient(group.header);
      const project = !client ? resolveProject(group.header) : null;
      for (const task of group.tasks) {
        const type = targetClientId === "action" ? "action" : client ? "client" : project ? "project" : "action";
        await addDoc(collection(db,"akz_tasks"), {
          task, detail:"", who:[who], priority:"MID", date:today(), dueDate:"",
          type, clientId:client?.id||"", projectId:project?.id||"", done:false
        });
      }
    }
  };
  const handleLogout = async () => {
    await signOut(auth);
  };

  // ─── Auth loading ─────────────────────────────────────────
  if (authLoading) return (
    <>
      <style>{`* { box-sizing:border-box; margin:0; padding:0; } body { background:#0A0908; }`}</style>
      <div style={{ minHeight:"100vh", background:"#0A0908", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ fontFamily:"'Archivo Black', sans-serif", fontSize:22, color:"#222", letterSpacing:2 }}>...</div>
      </div>
    </>
  );

  if (!user) return <LoginScreen onAuth={() => {}} />;

  const owner = isOwner(user.uid);
  const allTasks = mode==="home" ? homeTasks : akzTasks;

  // Collaborator: filter clients/projects where their UID is in members
  const visibleClients  = owner ? clients  : clients.filter(c  => (c.members||[]).includes(user.uid));
  const visibleProjects = owner ? projects : projects.filter(p => (p.members||[]).includes(user.uid));
  const visibleAkzTasks = owner ? akzTasks : akzTasks.filter(t => whoArr(t.who).includes(user.uid));

  // Collab welcome screen — shown once per session for non-owners
  if (!owner && !loading && showWelcome) {
    const me = collabs.find(c => c.uid === user.uid);
    const myTaskCount = visibleAkzTasks.filter(t => whoArr(t.who).includes(user.uid) && !t.done).length;
    return <CollabWelcome
      collab={{ name: me?.name || user.email, uid: user.uid, taskCount: myTaskCount }}
      onContinue={() => setShowWelcome(false)}
    />;
  }

  // Collabs visible to current context (for Who? selector)
  const contextCollabs = collabs;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&subset=latin,latin-ext&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        body { background:#0A0908; overflow-x:hidden; }
        html { overflow-x:hidden; }
        ::-webkit-scrollbar { width:3px; }
        ::-webkit-scrollbar-thumb { background:#222; }
        input, select { -webkit-appearance:none; }
      `}</style>

      {showHomeAdd   && <HomeTaskModal onClose={()=>setShowHomeAdd(false)} onSave={addHome}/>}
      {editHomeTask  && <HomeTaskModal onClose={()=>setEditHomeTask(null)} onSave={saveHome} initial={editHomeTask}/>}
      {showAkzAdd    && <AKZTaskModal  onClose={()=>setShowAkzAdd(false)}  onSave={addAkz} clients={clients} projects={projects} collabs={contextCollabs} defaultType={akzAddType} isEdit={false} initial={{task:"",detail:"",who:"Keops",priority:"MID",date:today(),type:akzAddType,clientId:akzAddClientId,projectId:akzAddProjectId}}/>}
      {editAkzTask   && <AKZTaskModal  onClose={()=>setEditAkzTask(null)}  onSave={saveAkz} clients={clients} projects={projects} collabs={contextCollabs} isEdit={true} initial={editAkzTask}/>}
      {showNewClient  && <NewClientModal  onClose={()=>setShowNewClient(false)}  onSave={addClient}/>}
      {showNewProject && <NewProjectModal onClose={()=>setShowNewProject(false)} onSave={addProject}/>}
      {editClient     && <EditClientModal  client={editClient}  onClose={()=>setEditClient(null)}  onSave={saveClient}/>}
      {editProject    && <EditProjectModal project={editProject} onClose={()=>setEditProject(null)} onSave={saveProject}/>}
      {manageMembersClient  && <ManageMembersModal label={manageMembersClient.name}  color={manageMembersClient.color||CLIENT_DEFAULT_COLOR} members={manageMembersClient.members||[]}  collabs={collabs} onClose={()=>setManageMembersClient(null)}  onSave={saveClientMembers}/>}
      {manageMembersProject && <ManageMembersModal label={manageMembersProject.name} color={AKZ_ACCENT}                                        members={manageMembersProject.members||[]} collabs={collabs} onClose={()=>setManageMembersProject(null)} onSave={saveProjectMembers}/>}

      <div style={{minHeight:"100vh",background:"#0A0908",color:"#F0EDE8",fontFamily:"'Inter', sans-serif",paddingBottom:100,maxWidth:680,margin:"0 auto",overflowX:"hidden",width:"100%"}}>

        {/* ── HEADER ── */}
        <div style={{position:"sticky",top:0,zIndex:10,background:"#0A0908",borderBottom:"3px solid #1e1e1e",width:"100%"}}>
          <div style={{padding:"20px 16px 12px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"baseline",lineHeight:1}}>
                <span style={{fontFamily:"'Archivo Black', sans-serif",fontSize:38,color:"#F0EDE8",letterSpacing:-1}}>ADULTING</span>
                <span style={{fontFamily:"'DM Mono', monospace",fontSize:15,color:ANA_COLOR,marginLeft:4,alignSelf:"flex-end",paddingBottom:6}}>.monster</span>
              </div>
              <div style={{marginTop:6,display:"flex",alignItems:"center",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}><AvatarAna size={22}/><span style={{fontSize:12,color:ANA_COLOR,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>Ana</span></div>
                <span style={{fontSize:10,color:"#333"}}>×</span>
                <div style={{display:"flex",alignItems:"center",gap:6}}><AvatarKeops size={22}/><span style={{fontSize:12,color:"#FFD93D",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>Keops</span></div>
                {/* Logout — subtle, always visible */}
                <button onClick={handleLogout} style={{marginLeft:"auto",background:"none",border:"1px solid #2a2a2a",color:"#333",fontSize:9,padding:"2px 8px",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1}}>LOGOUT</button>
              </div>

              <div style={{display:"flex",gap:0,marginTop:12}}>
                {owner && <button onClick={()=>setMode("home")} style={{padding:"5px 14px",border:`2px solid ${mode==="home"?"#F0EDE8":"#2a2a2a"}`,background:mode==="home"?"#F0EDE8":"transparent",color:mode==="home"?"#000":"#444",fontFamily:"'Archivo Black', sans-serif",fontSize:11,letterSpacing:1.5,textTransform:"uppercase",cursor:"pointer"}}>🏠 HOME</button>}
                <button onClick={()=>setMode("akz")} style={{padding:"5px 14px",border:`2px solid ${mode==="akz"?AKZ_ACCENT:"#2a2a2a"}`,background:mode==="akz"?AKZ_ACCENT+"22":"transparent",color:mode==="akz"?AKZ_ACCENT:"#444",fontFamily:"'Archivo Black', sans-serif",fontSize:11,letterSpacing:1.5,textTransform:"uppercase",cursor:"pointer"}}>⚡ AKZ</button>
                {!owner && <span style={{marginLeft:8,padding:"5px 10px",fontSize:9,color:"#444",fontFamily:"'Archivo Black', sans-serif",letterSpacing:1,border:"1px solid #1a1a1a",alignSelf:"center"}}>COLLABORATOR</span>}
              </div>
            </div>
            <RadialProgress tasks={allTasks}/>
          </div>
        </div>

        {/* ── CONTENT ── */}
        <div style={{padding:"16px 12px 0"}}>
          {loading ? (
            <div style={{textAlign:"center",padding:"60px 20px"}}>
              <div style={{fontFamily:"'Archivo Black', sans-serif",fontSize:22,color:"#333",letterSpacing:2}}>LOADING...</div>
            </div>
          ) : mode==="home" && owner ? (
            <HomeView tasks={homeTasks} onToggle={toggleHome} onDelete={deleteHome} onEdit={setEditHomeTask}/>
          ) : (
            <AKZView tasks={visibleAkzTasks} clients={visibleClients} projects={visibleProjects} collabs={contextCollabs} onToggle={toggleAkz} onDelete={deleteAkz} onEdit={setEditAkzTask} onAddClient={()=>setShowNewClient(true)} onAddProject={()=>setShowNewProject(true)} onAddTask={handleAddAkzTask} onEditClient={setEditClient} onEditProject={setEditProject} onManageClientMembers={setManageMembersClient} onManageProjectMembers={setManageMembersProject} onImport={handleImport} isOwner={owner}/>
          )}
        </div>

        {/* Footer */}
        <div style={{padding:"32px 20px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontFamily:"'DM Mono', monospace",fontSize:10,color:"#2a2a2a"}}>adulting.monster</span>
          <span style={{fontSize:10,color:"#222"}}>v0.10</span>
        </div>
      </div>

      {/* FAB */}
      <button onClick={()=>mode==="home"?setShowHomeAdd(true):handleAddAkzTask("action")} style={{position:"fixed",bottom:28,right:20,width:52,height:52,background:mode==="akz"?AKZ_ACCENT:ANA_COLOR,border:"none",cursor:"pointer",fontFamily:"'Archivo Black', sans-serif",fontSize:28,color:"#000",lineHeight:1,boxShadow:`0 4px 24px ${mode==="akz"?"rgba(168,255,120,0.3)":"rgba(126,184,201,0.4)"}`,zIndex:50,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
    </>
  );
}
